'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { Download, QrCode, Check } from 'lucide-react';

import QRCodeStyling from 'qr-code-styling';

// ---------------------------------------------------------------------------
// Типы шаблонов
// ---------------------------------------------------------------------------

interface QrTemplate {
  /** Иконка шаблона — эмодзи. */
  icon: string;
  /** Превращает введённый текст в данные QR-кода. */
  toData: (input: string) => string;
}

type TemplateId = keyof typeof TEMPLATES;

const LABELS: { id: TemplateId; icon: string }[] = [
  { id: 'url', icon: '🔗' },
  { id: 'text', icon: '📝' },
  { id: 'phone', icon: '📞' },
  { id: 'email', icon: '✉️' },
  { id: 'sms', icon: '💬' },
  { id: 'wifi', icon: '📶' },
  { id: 'vcard', icon: '👤' },
  { id: 'crypto', icon: '🪙' },
  { id: 'geo', icon: '📍' },
];

const TEMPLATES = {
  url: {
    icon: '🔗',
    toData: (v: string) =>
      v.startsWith('http://') || v.startsWith('https://') ? v : `https://${v}`,
  } satisfies QrTemplate,
  text: {
    icon: '📝',
    toData: (v: string) => v,
  } satisfies QrTemplate,
  phone: {
    icon: '📞',
    toData: (v: string) => {
      const digits = v.replace(/\D/g, '');
      if (!digits) return 'tel:';
      return digits.startsWith('+') ? `tel:${digits}` : `tel:+${digits}`;
    },
  } satisfies QrTemplate,
  email: {
    icon: '✉️',
    toData: (v: string) => `mailto:${v}`,
  } satisfies QrTemplate,
  sms: {
    icon: '💬',
    toData: (v: string) => {
      const [phone = '', ...rest] = v.split('|');
      const msg = rest.join('|');
      return msg
        ? `sms:${phone.trim()}?body=${encodeURIComponent(msg.trim())}`
        : `sms:${phone.trim()}`;
    },
  } satisfies QrTemplate,
  wifi: {
    icon: '📶',
    toData: (v: string) => {
      const [ssid = '', pass = '', enc = 'WPA'] = v.split('|').map((s) => s.trim());
      return `WIFI:T:${enc};S:${ssid};P:${pass};;`;
    },
  } satisfies QrTemplate,
  vcard: {
    icon: '👤',
    toData: (v: string) => {
      const [name = '', phone = '', email = '', org = ''] = v.split('|').map((s) => s.trim());
      const lines = ['BEGIN:VCARD', 'VERSION:3.0'];
      if (name) lines.push(`FN:${name}`, `N:${name};;;`);
      if (phone) lines.push(`TEL:${phone}`);
      if (email) lines.push(`EMAIL:${email}`);
      if (org) lines.push(`ORG:${org}`);
      lines.push('END:VCARD');
      return lines.join('\n');
    },
  } satisfies QrTemplate,
  crypto: {
    icon: '🪙',
    toData: (v: string) => {
      const [addr = '', amount, label] = v.split('|').map((s) => s.trim());
      if (addr.startsWith('bc1') || addr.startsWith('1') || addr.startsWith('3')) {
        let uri = `bitcoin:${addr}`;
        const params: string[] = [];
        if (amount) params.push(`amount=${amount}`);
        if (label) params.push(`label=${encodeURIComponent(label)}`);
        if (params.length) uri += `?${params.join('&')}`;
        return uri;
      }
      if (addr.startsWith('0x')) {
        let uri = `ethereum:${addr}`;
        const params: string[] = [];
        if (amount) params.push(`value=${amount}`);
        if (label) params.push(`label=${encodeURIComponent(label)}`);
        if (params.length) uri += `?${params.join('&')}`;
        return uri;
      }
      return addr;
    },
  } satisfies QrTemplate,
  geo: {
    icon: '📍',
    toData: (v: string) => {
      const [lat = '', lng = '', label] = v.split('|').map((s) => s.trim());
      return `geo:${lat},${lng}${label ? `?q=${encodeURIComponent(label)}` : ''}`;
    },
  } satisfies QrTemplate,
} as const;

// ---------------------------------------------------------------------------
// Компонент
// ---------------------------------------------------------------------------

export function QrCodeGenerator({ title }: { title: string }) {
  const t = useTranslations('dh_qr');
  const containerRef = useRef<HTMLDivElement>(null);
  const qrRef = useRef<InstanceType<typeof QRCodeStyling> | null>(null);

  const [template, setTemplate] = useState<TemplateId>('url');
  const [input, setInput] = useState('');
  const [fgColor, setFgColor] = useState('#000000');
  const [bgColor, setBgColor] = useState('#ffffff');
  const [cornerStyle, setCornerStyle] = useState<'square' | 'dots' | 'extra-rounded'>('square');
  const [dotStyle, setDotStyle] = useState<
    'square' | 'dots' | 'rounded' | 'classy' | 'classy-rounded' | 'extra-rounded'
  >('square');
  const [copied, setCopied] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  const generateQr = useCallback(() => {
    const data = input.trim() ? TEMPLATES[template].toData(input.trim()) : '';

    const options: Record<string, unknown> = {
      width: 400,
      height: 400,
      data: data || 'https://datahunter.store',
      type: 'canvas',
      image: '',
      margin: 10,
      qrOptions: { typeNumber: 0, mode: 'Byte', errorCorrectionLevel: 'M' },
      dotsOptions: { type: dotStyle, color: fgColor },
      backgroundOptions: { color: bgColor },
      cornersSquareOptions: { type: cornerStyle },
      cornersDotOptions: { type: cornerStyle === 'dots' ? 'dots' : 'square' },
    };

    if (!qrRef.current) {
      qrRef.current = new QRCodeStyling(options);
    } else {
      qrRef.current.update(options);
    }

    if (containerRef.current) {
      containerRef.current.innerHTML = '';
      qrRef.current.append(containerRef.current);
    }
  }, [input, template, fgColor, bgColor, cornerStyle, dotStyle]);

  // Генерируем при изменении параметров
  useEffect(() => {
    generateQr();
  }, [generateQr]);

  const handleDownload = useCallback((ext: 'png' | 'svg') => {
    if (!qrRef.current) return;
    qrRef.current.download({ name: 'qrcode', extension: ext });
    setDownloaded(true);
    setTimeout(() => {
      setDownloaded(false);
    }, 2000);
  }, []);

  const handleCopy = useCallback(() => {
    if (!qrRef.current || !containerRef.current) return;
    const canvas = containerRef.current.querySelector('canvas');
    if (canvas) {
      canvas.toBlob((blob: Blob | null) => {
        if (!blob) return;
        void navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]).then(() => {
          setCopied(true);
          setTimeout(() => {
            setCopied(false);
          }, 2000);
        });
      });
    }
  }, []);

  return (
    <div className="max-w-[960px] mx-auto px-6 py-10">
      <h1 className="text-2xl font-bold mb-8">{title}</h1>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Левая колонка: настройки */}
        <div>
          {/* Шаблоны */}
          <div className="mb-6">
            <label className="text-sm font-medium text-[var(--color-text-muted)] mb-3 block">
              {t('template')}
            </label>
            <div className="grid grid-cols-4 sm:grid-cols-4 gap-2">
              {LABELS.map(({ id, icon }) => (
                <button
                  key={id}
                  onClick={() => {
                    setTemplate(id);
                    setInput('');
                  }}
                  data-active={template === id}
                  className="flex flex-col items-center gap-1 px-2 py-3 rounded-xl border text-xs transition-colors data-[active=true]:border-[var(--color-text)] data-[active=true]:bg-[var(--color-bg-elevated)] border-[var(--color-border)] hover:border-[var(--color-text-muted)]"
                >
                  <span className="text-lg">{icon}</span>
                  <span className="text-[10px] leading-tight text-center text-[var(--color-text-muted)]">
                    {t(`template_${id}`)}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Поле ввода */}
          <div className="mb-6">
            <label className="text-sm font-medium text-[var(--color-text-muted)] mb-2 block">
              {t(`template_${template}_label`)}
            </label>
            <input
              type="text"
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
              }}
              placeholder={t(`template_${template}_placeholder`)}
              className="w-full px-4 py-2.5 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] text-sm focus:outline-none focus:border-[var(--color-text)] transition-colors"
            />
            {template === 'sms' && (
              <p className="mt-1.5 text-xs text-[var(--color-text-muted)]">{t('sms_hint')}</p>
            )}
            {template === 'wifi' && (
              <p className="mt-1.5 text-xs text-[var(--color-text-muted)]">{t('wifi_hint')}</p>
            )}
            {template === 'vcard' && (
              <p className="mt-1.5 text-xs text-[var(--color-text-muted)]">{t('vcard_hint')}</p>
            )}
            {template === 'crypto' && (
              <p className="mt-1.5 text-xs text-[var(--color-text-muted)]">{t('crypto_hint')}</p>
            )}
            {template === 'geo' && (
              <p className="mt-1.5 text-xs text-[var(--color-text-muted)]">{t('geo_hint')}</p>
            )}
          </div>

          {/* Стилизация */}
          <div className="mb-6">
            <label className="text-sm font-medium text-[var(--color-text-muted)] mb-3 block">
              {t('style')}
            </label>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-xs text-[var(--color-text-muted)] mb-1.5 block">
                  {t('fg_color')}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={fgColor}
                    onChange={(e) => {
                      setFgColor(e.target.value);
                    }}
                    className="w-10 h-10 rounded-lg border border-[var(--color-border)] cursor-pointer"
                  />
                  <span className="text-xs font-mono text-[var(--color-text-muted)]">
                    {fgColor}
                  </span>
                </div>
              </div>
              <div>
                <label className="text-xs text-[var(--color-text-muted)] mb-1.5 block">
                  {t('bg_color')}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={bgColor}
                    onChange={(e) => {
                      setBgColor(e.target.value);
                    }}
                    className="w-10 h-10 rounded-lg border border-[var(--color-border)] cursor-pointer"
                  />
                  <span className="text-xs font-mono text-[var(--color-text-muted)]">
                    {bgColor}
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-[var(--color-text-muted)] mb-1.5 block">
                  {t('corner_style')}
                </label>
                <select
                  value={cornerStyle}
                  onChange={(e) => {
                    setCornerStyle(e.target.value as typeof cornerStyle);
                  }}
                  className="w-full px-3 py-2 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] text-sm focus:outline-none focus:border-[var(--color-text)] transition-colors"
                >
                  <option value="square">{t('corner_square')}</option>
                  <option value="dots">{t('corner_dots')}</option>
                  <option value="extra-rounded">{t('corner_extra_rounded')}</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-[var(--color-text-muted)] mb-1.5 block">
                  {t('dot_style')}
                </label>
                <select
                  value={dotStyle}
                  onChange={(e) => {
                    setDotStyle(e.target.value as typeof dotStyle);
                  }}
                  className="w-full px-3 py-2 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] text-sm focus:outline-none focus:border-[var(--color-text)] transition-colors"
                >
                  <option value="square">{t('dot_square')}</option>
                  <option value="dots">{t('dot_dots')}</option>
                  <option value="rounded">{t('dot_rounded')}</option>
                  <option value="classy">{t('dot_classy')}</option>
                  <option value="classy-rounded">{t('dot_classy_rounded')}</option>
                  <option value="extra-rounded">{t('dot_extra_rounded')}</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Правая колонка: QR-код */}
        <div>
          <div className="flex items-center justify-center p-8 rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-card)] min-h-[400px]">
            <div ref={containerRef} className="flex items-center justify-center" />
          </div>

          {/* Кнопки действий */}
          <div className="flex flex-wrap gap-3 mt-6">
            <button
              onClick={handleCopy}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-[var(--color-border)] hover:border-[var(--color-text-muted)] text-sm transition-colors"
            >
              {copied ? <Check className="w-4 h-4" /> : <QrCode className="w-4 h-4" />}
              {copied ? t('copied') : t('copy_qr')}
            </button>
            <button
              onClick={() => {
                handleDownload('png');
              }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-[var(--color-border)] hover:border-[var(--color-text-muted)] text-sm transition-colors"
            >
              {downloaded ? <Check className="w-4 h-4" /> : <Download className="w-4 h-4" />}
              {downloaded ? t('downloaded') : t('download_png')}
            </button>
            <button
              onClick={() => {
                handleDownload('svg');
              }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-[var(--color-border)] hover:border-[var(--color-text-muted)] text-sm transition-colors"
            >
              <Download className="w-4 h-4" />
              {t('download_svg')}
            </button>
          </div>

          <p className="mt-4 text-xs text-[var(--color-text-muted)] leading-relaxed">
            {t('privacy_note')}
          </p>
        </div>
      </div>
    </div>
  );
}
